import * as React from 'react';
import { GDSResponsiveProp, Tabs } from '@leagueplatform/genesis-core';
import {
  MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { MasonryLeadingIconWithoutEmoji } from '#common/leading-icon/leading-icon.type.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { addToActionAnalytics } from '../../utils/addToActionAnalytics';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export interface MasonryTabListItem {
  title: string;
  id: string;
  leadingIcon?: MasonryLeadingIconWithoutEmoji;
}
export interface MasonryTabsNodeProps {
  defaultSelectedTabId: string;
  tabList: MasonryTabListItem[];
  tabListName: string;
  tabDistribution?: GDSResponsiveProp<'distributeEvenly' | 'hugContents'>;
}

export type MasonryTabsNodeActions = {
  onClick?: MasonryDriverAction;
};

export type MasonryTabsNode = MasonryEngineNode<
  'tabs',
  MasonryTabsNodeProps,
  MasonryTabsNodeActions,
  { items: MasonryEngineNode<string, any>[] }
>;

export type MasonryTabsRendererProps =
  MasonryEngineNodeRendererProps<MasonryTabsNode>;

export const MasonryTabsRenderer = ({
  items,
  defaultSelectedTabId,
  tabList = [],
  tabListName,
  tabDistribution = 'hugContents',
  onClick,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
}: MasonryTabsRendererProps) => {
  const invokeAction = useInvokeAction();
  const initialTabId = defaultSelectedTabId || tabList[0]?.id;
  /**
   * We keep track of which tab panels have been viewed/visited by the user
   * at least once (including the initial/default one). We do this so that we can
   * add the `forceMount` prop to the corresponding `<Tabs.TabPanel>` element, which
   * tells the underlying `@radix-ui/react-tabs` library to **keep this tab panel mounted**
   * even when it is not the currently focused panel (we then use `hidden` to visually and
   * semantically exclude it from the user's experience). This, in turn, prevents the
   * Masonry Nodes inside the panel from being unmounted and thus ensures their node
   * states are preserved when switching between tabs.
   */
  const [visitedTabPanels, setVisitedTabPanels] = React.useState(
    new Set([initialTabId]),
  );
  const [selectedTabId, setSelectedTabId] = React.useState(initialTabId);

  if (!items || !tabList.length) return null;

  const handleOnClick = (
    tabId: string,
    tabName: string,
    event: React.SyntheticEvent,
  ) => {
    if (!onClick) return;

    invokeAction({
      event,
      ...addToActionAnalytics(onClick, {
        tab_id: tabId,
        content: { name: tabName },
      }),
    });
  };

  return (
    <Tabs.Root
      ref={rootElementRef}
      data-testid={locatorId}
      value={selectedTabId}
      onValueChange={(tabId) => {
        setVisitedTabPanels((prev) => new Set([...prev, tabId]));
        setSelectedTabId(tabId);
      }}
      className={formatClassNames(engineClassName, className)}
      css={css}
    >
      <Tabs.TabList
        aria-label={tabListName ?? undefined}
        divider
        tabDistribution={tabDistribution}
      >
        {tabList.map((tab) => (
          <Tabs.Tab
            key={`${tab.id}-tab-button`}
            value={tab.id}
            onClick={(event) => {
              handleOnClick(tab.id, tab.title, event);
            }}
            icon={tab.leadingIcon?.value}
            iconLabel={tab.leadingIcon?.iconAltText}
          >
            {tab.title}
          </Tabs.Tab>
        ))}
      </Tabs.TabList>
      {React.Children.toArray(items).map((item, index) => {
        // Do not render a tab panel that does not have a matching tab
        if (!tabList[index]?.id) return null;

        const isHidden = tabList[index].id !== selectedTabId;

        return (
          <Tabs.TabPanel
            key={`${tabList[index].id}-tabpanel`}
            value={tabList[index].id}
            forceMount={visitedTabPanels.has(tabList[index].id) || undefined}
            hidden={isHidden}
            aria-hidden={isHidden}
          >
            {item}
          </Tabs.TabPanel>
        );
      })}
    </Tabs.Root>
  );
};
